import moment from 'moment';

export type AiMessageRole = 'admin' | 'ai';

export interface AiConversation {
	conversationId: string;
	adminId?: string;
	title?: string;
	aiName?: string;
	aiModel?: string;
	lastMessage?: string;
	lastMessageAt?: string;
	createdAt?: string;
	updatedAt?: string;
}

export interface AiConversationMessage {
	messageId: string;
	conversationId: string;
	role: AiMessageRole;
	content: string;
	requiresDatabase?: boolean;
	metadata?: {
		error?: boolean;
		message?: string;
		stage?: string;
		requiresRevenueApi?: boolean;
		requiresChatExport?: boolean;
		revenueApi?: string;
		revenueApiParams?: Record<string, unknown>;
		chatExportParams?: Record<string, unknown>;
		[key: string]: unknown;
	};
	aiModel?: string;
	totalTokens?: number;
	createdAt?: string;
	updatedAt?: string;
}

export interface AiConversationDetail extends AiConversation {
	messages: AiConversationMessage[];
}

export interface AiAskResponse {
	conversation: AiConversation;
	adminMessage: { role: AiMessageRole; content: string };
	aiMessage: AiConversationMessage;
}

export interface PaginatedListResponse<T> {
	items?: T[];
	pagination?: {
		total?: number;
		currentPageItems?: number;
	};
	data?: {
		items?: T[];
		pagination?: {
			total?: number;
			currentPageItems?: number;
		};
	};
}

export const parseListItems = <T>(response: unknown): T[] => {
	if (!response) return [];
	if (Array.isArray(response)) return response as T[];

	const res = response as PaginatedListResponse<T>;
	return res.items ?? res.data?.items ?? [];
};

export const parsePagination = (response: unknown): { total: number; currentPageItems: number } => {
	const res = response as PaginatedListResponse<unknown>;
	const pagination = res.pagination ?? res.data?.pagination;
	return {
		total: pagination?.total ?? 0,
		currentPageItems: pagination?.currentPageItems ?? 0
	};
};

export const formatConversationTime = (value?: string): string => {
	if (!value) return '';
	const m = moment(value);
	return m.isValid() ? m.format('MMM D, h:mm A') : '';
};

export const formatMessageTime = (value?: string): string => {
	if (!value) return '';
	const m = moment(value);
	return m.isValid() ? m.format('h:mm A') : '';
};

export const getConversationTitle = (conversation: AiConversation): string =>
	conversation.title?.trim() || 'Untitled conversation';

export const getConversationPreview = (conversation: AiConversation): string =>
	conversation.lastMessage?.trim() || 'No messages yet';

export const isAiMessageError = (message: AiConversationMessage): boolean =>
	message.role === 'ai' && message.metadata?.error === true;

export const getAiMessageErrorText = (message: AiConversationMessage): string =>
	message.metadata?.message?.trim() || message.content?.trim() || 'Failed to process AI request';

export const AI_ASSISTANT_PATH = '/ai-assistant';
export const AI_URL_PARAM_CONVERSATION_ID = 'conversationId';
export const AI_URL_PARAM_MESSAGE_ID = 'messageId';
export const AI_MESSAGE_LINK_HIGHLIGHT_MS = 5000;

export const canShareAiMessageLink = (message: AiConversationMessage): boolean =>
	!message.messageId.startsWith('temp-admin-') && !message.messageId.startsWith('persisted-admin-');

export const buildAiMessageLink = (conversationId: string, messageId: string): string => {
	const url = new URL(AI_ASSISTANT_PATH, window.location.origin);
	url.searchParams.set(AI_URL_PARAM_CONVERSATION_ID, conversationId);
	url.searchParams.set(AI_URL_PARAM_MESSAGE_ID, messageId);
	return url.toString();
};
